import ExcelUpload from "../../../api/excelUpload";
import { Page } from "../../../interfaces/PagesInterface";
import { ProjectTasksComparison, UploadType } from "../../../interfaces/ExcelUploadInterface";

/**
 * WCDP project-import variants.
 *
 * The five WCDP import pages (Paint SF / Paint MF / Paint MF V1 / Drywall SF /
 * Drywall MF) are the same preview→apply flow against five different endpoint
 * families. `WcdpProjectImport` renders all of them; this table is the only
 * thing that differs between them.
 *
 * Two behaviours fork here and are deliberately preserved from the v1 pages:
 *   • Paint needs a worksheet chosen before comparing; Drywall does not.
 *   • Paint MF V1 BLOCKS apply when the file would remove intermediates that are
 *     not safe to remove; Paint SF/MF and Drywall warn and allow it.
 */

export type WcdpImportVariantKey =
    | "paintSF"
    | "paintMF"
    | "paintMFV1"
    | "drywallSF"
    | "drywallMF"
    | "upfPiecePdf";

export interface WcdpImportVariant {
    key: WcdpImportVariantKey;
    /** Page enum member — doubles as the i18n key for the page title. */
    pageKey: Page;
    /** Paint workbooks need a worksheet chosen before comparing. */
    needsWorksheet: boolean;
    /** Worksheet-name probe; null for the Drywall variants. */
    fetchSheetNames: ((file: File) => Promise<string[]>) | null;
    /** Preview endpoint — never writes. */
    compare: (
        file: File,
        projectID: string,
        sheetName?: string,
    ) => Promise<ProjectTasksComparison | null>;
    /** Apply endpoint (the UploadType enum value IS the URL). */
    applyUploadType: UploadType;
    /** true → unsafe removals block apply (Paint MF V1 only); false → warn and allow. */
    blockUnsafeRemovals: boolean;
    /** Short line under the page title. */
    subtitle: string;
    /** Copy for the empty dropzone. */
    dropTitle: string;
    /** react-dropzone accept map; omitted → the Excel workbook map. */
    accept?: Record<string, string[]>;
    /** Hint under the empty dropzone; omitted → the ".xls/.xlsx/.xlsm workbook" line. */
    fileHint?: string;
    /** What the uploaded file is called in the page copy; omitted → "workbook". */
    fileNoun?: string;
}

export const WCDP_IMPORT_VARIANTS: Record<WcdpImportVariantKey, WcdpImportVariant> = {
    paintSF: {
        key: "paintSF",
        pageKey: Page.WCDPPaintSingleFamilyImport,
        needsWorksheet: true,
        fetchSheetNames: (file) => ExcelUpload.excelSFSheetNames(file),
        compare: (file, projectID, sheetName) =>
            ExcelUpload.uploadPaintSFExcelComparison(file, projectID, sheetName ?? ""),
        applyUploadType: UploadType.PaintSF,
        blockUnsafeRemovals: false,
        subtitle: "Drop a single-family paint workbook to preview and apply task changes.",
        dropTitle: "Drop the paint single-family workbook",
    },
    paintMF: {
        key: "paintMF",
        pageKey: Page.WCDPPaintMultiFamilyImport,
        needsWorksheet: true,
        fetchSheetNames: (file) => ExcelUpload.excelMFSheetNames(file),
        compare: (file, projectID, sheetName) =>
            ExcelUpload.uploadPaintMFExcelComparison(file, projectID, sheetName ?? ""),
        applyUploadType: UploadType.PaintMF,
        blockUnsafeRemovals: false,
        subtitle: "Drop a multi-family paint workbook to preview and apply task changes.",
        dropTitle: "Drop the paint multi-family workbook",
    },
    paintMFV1: {
        key: "paintMFV1",
        pageKey: Page.WCDPPaintMultiFamilyImportV1,
        needsWorksheet: true,
        // Shares the multi-family sheet-name probe with paintMF by design.
        fetchSheetNames: (file) => ExcelUpload.excelMFSheetNames(file),
        compare: (file, projectID, sheetName) =>
            ExcelUpload.uploadPaintMFExcelComparisonV1(file, projectID, sheetName ?? ""),
        applyUploadType: UploadType.PaintMFV1,
        blockUnsafeRemovals: true,
        subtitle: "Drop a multi-family paint workbook (V1 layout) to preview and apply task changes.",
        dropTitle: "Drop the paint multi-family (V1) workbook",
    },
    drywallSF: {
        key: "drywallSF",
        pageKey: Page.WCDPDrywallSingleFamilyImport,
        needsWorksheet: false,
        fetchSheetNames: null,
        compare: (file, projectID) =>
            ExcelUpload.uploadDrywallSFExcelComparison(file, projectID),
        applyUploadType: UploadType.DrywallSF,
        blockUnsafeRemovals: false,
        subtitle: "Drop a single-family drywall workbook to preview and apply task changes.",
        dropTitle: "Drop the drywall single-family workbook",
    },
    drywallMF: {
        key: "drywallMF",
        pageKey: Page.WCDPDrywallMultiFamilyImport,
        needsWorksheet: false,
        fetchSheetNames: null,
        compare: (file, projectID) =>
            ExcelUpload.uploadDrywallMFExcelComparison(file, projectID),
        applyUploadType: UploadType.DrywallMF,
        blockUnsafeRemovals: false,
        subtitle: "Drop a multi-family drywall workbook to preview and apply task changes.",
        dropTitle: "Drop the drywall multi-family workbook",
    },
    // UPF (United Production Framing) — not a WCDP page, but the same
    // preview→apply flow, fed a phase-budget PDF instead of a workbook.
    upfPiecePdf: {
        key: "upfPiecePdf",
        pageKey: Page.UPFPieceBudgetImport,
        needsWorksheet: false,
        fetchSheetNames: null,
        compare: (file, projectID) =>
            ExcelUpload.uploadUpfPiecePdfComparison(file, projectID),
        applyUploadType: UploadType.UpfPiecePdf,
        blockUnsafeRemovals: false,
        subtitle: "Drop the UPF phase budget PDF (MASTER LABOR PHASE … BUDGET) to preview and apply piece changes by lot.",
        dropTitle: "Drop the UPF phase budget PDF",
        accept: { "application/pdf": [".pdf"] },
        fileHint: "Accepts a single .pdf phase budget (MASTER LABOR PHASE … BUDGET).",
        fileNoun: "PDF",
    },
};
